import { ChevronLeft, MessageSquare, X } from "lucide-react";
import { useEffect, useState } from "react";
import { ChatThread } from "@/components/chat/ChatThread";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { BestFitProfilesPanel } from "./BestFitProfilesPanel";
import { ConfidenceBadge } from "./ConfidenceBadge";
import { ScoreDetailBody, type ScoreDetailSummary } from "./ScoreDetailBody";
import { ScoreRing } from "./score-viz";
import { WhyThisScore } from "./WhyThisScore";

interface ScoreDetailSidePanelProps {
  open: boolean;
  onClose: () => void;
  campaignId: string;
  locationId: string;
  addressRaw: string;
  city: string | null;
  floScore: number | null;
  confidence: string | null;
}

export function ScoreDetailSidePanel({
  open,
  onClose,
  campaignId,
  locationId,
  addressRaw,
  city,
  floScore,
  confidence,
}: ScoreDetailSidePanelProps) {
  const [summary, setSummary] = useState<ScoreDetailSummary | null>(null);
  const [view, setView] = useState<"details" | "chat">("details");

  // Reset cached summary + return to the details view when the panel targets a
  // different location.
  // biome-ignore lint/correctness/useExhaustiveDependencies: reset on location change
  useEffect(() => {
    setSummary(null);
    setView("details");
  }, [locationId]);

  const metaParts = [city];
  if (summary) metaParts.push(`${summary.metricsScored}/${summary.metricsTotal} metrics scored`);
  const meta = metaParts.filter(Boolean).join(" · ");

  return (
    <div
      className={cn(
        "absolute top-0 right-0 bottom-0 z-30 flex w-full flex-col border-l border-border bg-background shadow-xl transition-transform duration-200 ease-out sm:w-[480px]",
        open ? "translate-x-0" : "pointer-events-none translate-x-full",
      )}
      aria-hidden={!open}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      {view === "chat" ? (
        <>
          {/* Chat view — fills the panel; back returns to the metrics. */}
          <div className="flex shrink-0 items-center gap-2 border-b border-border p-3">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-8 gap-1.5 px-2"
              onClick={() => setView("details")}
            >
              <ChevronLeft className="h-4 w-4" />
              Metrics
            </Button>
            <div className="min-w-0 flex-1 text-center">
              <span className="font-mono text-[10.5px] uppercase tracking-[0.1em] text-muted-foreground">
                Ask Atlas
              </span>
            </div>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-7 w-7 shrink-0"
              onClick={onClose}
              aria-label="Close details"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
          <ChatThread campaignId={campaignId} onSideEffect={() => {}} />
        </>
      ) : (
        <>
          {/* Header */}
          <div className="shrink-0 border-b border-border p-4">
            <div className="flex items-center justify-between gap-2">
              {confidence ? (
                <ConfidenceBadge confidence={confidence} />
              ) : (
                <span className="text-[11px] text-muted-foreground" />
              )}
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-7 w-7 shrink-0"
                onClick={(e) => {
                  e.stopPropagation();
                  onClose();
                }}
                aria-label="Close details"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>

            <div className="mt-3 flex items-center gap-4">
              <ScoreRing score={floScore} size={64} />
              <div className="min-w-0 flex-1">
                <h2
                  className="text-sm font-semibold uppercase leading-snug tracking-tight"
                  title={addressRaw}
                >
                  {addressRaw || "—"}
                </h2>
                {meta && (
                  <p className="mt-1 font-mono text-[11px] uppercase tracking-[0.04em] text-muted-foreground">
                    {meta}
                  </p>
                )}
              </div>
            </div>

            <Button
              type="button"
              variant="outline"
              size="sm"
              className="mt-4 h-9 w-full justify-center gap-2"
              onClick={() => setView("chat")}
            >
              <MessageSquare className="h-4 w-4" />
              Ask Atlas about this site
            </Button>
          </div>

          {/* Scrollable body — why this score, best-fit profiles, category breakdown */}
          <div className="min-h-0 flex-1 overflow-y-auto p-4">
            {locationId && (
              <>
                <WhyThisScore active={open} campaignId={campaignId} locationId={locationId} />

                <BestFitProfilesPanel
                  active={open}
                  campaignId={campaignId}
                  locationId={locationId}
                />

                <div className="mb-3">
                  <p className="font-mono text-[10.5px] uppercase tracking-[0.1em] text-muted-foreground">
                    Category Breakdown
                    {summary &&
                      ` · ${summary.categoryCount} categories · ${summary.metricsScored}/${summary.metricsTotal} scored`}
                  </p>
                </div>
                <ScoreDetailBody
                  active={open}
                  campaignId={campaignId}
                  locationId={locationId}
                  onSummary={setSummary}
                />
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
}
